/**
 * Landing strip for a cross-host session transfer.
 *
 * Session cookies are host-only outside the platform zone — that is
 * what stops one tenant's page from acting against another tenant's
 * API with the same user's cookie. The consequence is that arriving on
 * a new host means arriving without a session, so the hop carries a
 * one-time token and this route exchanges it for a cookie *here*.
 *
 * Three things this component must get right:
 *
 * 1. **Redeem from the destination host.** The cookie lands wherever
 *    the request came from, so redeeming anywhere else would issue a
 *    session for the wrong place. Being mounted on the tenant shell is
 *    what guarantees that.
 * 2. **Get the token out of the URL immediately.** It is a bearer
 *    credential. It is already in history and in any Referer this page
 *    emits; ``replaceState`` before the next navigation keeps it from
 *    travelling further. Single use and a 60-second TTL are the real
 *    defence — this is the cheap extra.
 * 3. **Never follow ``next`` off-origin.** It arrives in a query
 *    string, so it is attacker-supplied by definition. Only a path is
 *    accepted; anything else falls back to the tenant root. A
 *    protocol-relative ``//evil.example`` is the case a naive
 *    ``startsWith("/")`` misses.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api";

/** A same-origin destination path, or ``null`` if it isn't one. */
export function safeNextPath(raw: string | null): string | null {
  if (!raw) return null;
  // The URL parser drops tab/CR/LF before resolving, so ``/<TAB>/host``
  // passes the prefix checks below and still lands on ``//host``.
  // No legitimate path carries a control character.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(raw)) return null;
  // ``//host`` and ``/\host`` are protocol-relative — they leave the
  // origin while still starting with a slash.
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return null;
  }
  return raw;
}

interface Props {
  /** Seam for tests — jsdom does not navigate. */
  onDone?: (path: string) => void;
}

export default function AuthContinue({ onDone }: Props): JSX.Element {
  const { t } = useTranslation("saas");
  const [failed, setFailed] = useState(false);
  // Redeem exactly once. The token is single-use server-side, so a
  // double invocation (StrictMode's deliberate double effect) would
  // burn it and then report failure on the second call.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");
    const next = safeNextPath(params.get("next")) ?? "/";

    // Strip the token before anything else can read or forward it.
    window.history.replaceState(null, "", window.location.pathname);

    if (!token) {
      setFailed(true);
      return;
    }

    apiFetch("/api/v1/auth/session-transfer/redeem", {
      method: "POST",
      body: { token },
    })
      .then(() => {
        if (onDone) onDone(next);
        // ``next`` passed ``safeNextPath`` above — a same-origin path only.
        // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
        else window.location.replace(next);
      })
      .catch(() => setFailed(true));
  }, [onDone]);

  if (failed) {
    return (
      <main
        className="mx-auto max-w-md px-6 py-16 text-center"
        data-testid="auth-continue-failed"
      >
        <h1 className="text-xl font-semibold text-ink">
          {t("authContinue.failedTitle")}
        </h1>
        <p className="mt-2 text-sm text-ink-2">{t("authContinue.failedBody")}</p>
      </main>
    );
  }

  return (
    <main
      className="mx-auto max-w-md px-6 py-16 text-center"
      data-testid="auth-continue"
      aria-live="polite"
    >
      <span className="mx-auto flex h-10 w-10 items-center justify-center">
        <span className="h-2.5 w-2.5 animate-ping rounded-full bg-accent" />
      </span>
      <p className="mt-4 text-sm text-ink-2">{t("authContinue.working")}</p>
    </main>
  );
}
